/**
 * Iter 67ba — Vendor-facing editable invoice viewer.
 *
 * Mounted as a route at `/vendor/invoices/:id`. Loads the invoice, lets the
 * vendor add/remove/edit line items, save drafts, issue, and settle.
 *
 * Line-item types render as a `Select` so vendors can categorise extras
 * (toll/fuel/cleaning/damage/mileage_overage) — the backend uses the kind
 * for analytics and downstream PDF rendering.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Send, Lock, FileText, History, Loader2 } from "lucide-react";
import Money from "@/components/Money";
import InvoiceDeliveryActions from "@/components/invoices/InvoiceDeliveryActions";

const LINE_KINDS = [
  { value: "rental",          label: "Rental" },
  { value: "addon",           label: "Add-on" },
  { value: "insurance",       label: "Insurance" },
  { value: "route_fee",       label: "Route fee" },
  { value: "meet_greet",      label: "Meet & Greet" },
  { value: "toll",            label: "Toll" },
  { value: "fuel",            label: "Fuel" },
  { value: "cleaning",        label: "Cleaning" },
  { value: "damage",          label: "Damage charge" },
  { value: "mileage_overage", label: "Mileage overage" },
  { value: "late_return",     label: "Late return fee" },
  { value: "discount",        label: "Discount" },
  { value: "custom",          label: "Other" },
];

function _newLine() {
  return {
    id: `new-${Math.random().toString(36).slice(2, 8)}`,
    kind: "custom",
    label: "",
    qty: 1,
    unit_price: 0,
    vat_rate: 0,
  };
}

function StatusPill({ status }) {
  const tones = {
    draft:     "bg-neutral-100 text-neutral-700",
    issued:    "bg-blue-50 text-blue-700",
    adjusted:  "bg-amber-50 text-amber-800",
    settled:   "bg-emerald-50 text-emerald-700",
    cancelled: "bg-rose-50 text-rose-700",
  };
  return (
    <Badge
      className={`uppercase tracking-wider text-[10px] font-bold ${tones[status] || tones.draft}`}
      variant="outline"
      data-testid={`invoice-status-${status}`}
    >
      {status}
    </Badge>
  );
}

export default function VendorInvoiceEditor() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [customerNote, setCustomerNote] = useState("");
  const [vatId, setVatId] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get(`/invoices/${id}`)
      .then((r) => {
        if (!alive) return;
        setInv(r.data);
        setItems(r.data.line_items || []);
        setCustomerNote(r.data.customer_note || "");
        setVatId(r.data.vat_id || "");
      })
      .catch((err) => {
        toast.error(formatApiError(err) || "Could not load invoice.");
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const li of items) {
      const lineNet = Number(li.qty || 0) * Number(li.unit_price || 0);
      const lineVat = lineNet * (Number(li.vat_rate || 0) / 100);
      net += lineNet;
      vat += lineVat;
    }
    return { subtotal_net: Math.round(net * 100) / 100, vat_total: Math.round(vat * 100) / 100, total_gross: Math.round((net + vat) * 100) / 100 };
  }, [items]);

  const editable = !!inv?.editable;

  const onAddLine = () => { setItems((arr) => [...arr, _newLine()]); setDirty(true); };
  const onRemoveLine = (idx) => { setItems((arr) => arr.filter((_, i) => i !== idx)); setDirty(true); };
  const onLineChange = (idx, key, val) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [key]: val } : it));
    setDirty(true);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/invoices/${id}`, {
        line_items: items.map((it) => ({
          ...it,
          qty: Number(it.qty || 0),
          unit_price: Number(it.unit_price || 0),
          vat_rate: Number(it.vat_rate || 0),
        })),
        customer_note: customerNote || null,
        vat_id: vatId || null,
      });
      setInv(r.data);
      setItems(r.data.line_items || []);
      setDirty(false);
      toast.success("Invoice saved.");
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onIssue = async () => {
    if (dirty) await onSave();
    setSaving(true);
    try {
      const r = await api.post(`/invoices/${id}/issue`);
      setInv(r.data);
      toast.success("Invoice issued — customer notified on next dashboard load.");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not issue invoice.");
    } finally {
      setSaving(false);
    }
  };

  const onSettle = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/invoices/${id}/settle`);
      setInv(r.data);
      toast.success("Invoice marked settled.");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not settle.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <main className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-neutral-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading invoice…</div>
        </main>
        <Footer />
      </div>
    );
  }
  if (!inv) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <main className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-neutral-500">Invoice not found.</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="vendor-invoice-editor">
        <Link to="/dashboard/vendor" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="font-display text-3xl font-medium flex items-center gap-3">
              <FileText className="h-7 w-7 text-indigo-600" />
              {inv.invoice_number}
              <StatusPill status={inv.status} />
            </h1>
            <div className="text-sm text-neutral-600 mt-1">
              Booking · <Link to={`/bookings/${inv.booking_id}`} className="text-blue-600 hover:underline">{inv.booking_id?.slice(0, 8)}</Link>
              {inv.issued_at && <> · issued {inv.issued_at.slice(0, 10)}</>}
            </div>
            {!editable && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200" data-testid="invoice-readonly-pill">
                <Lock className="h-3 w-3" />
                {inv.edit_lock_reason || "Read-only"}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* Iter 67bc — Download / Email / WhatsApp the invoice */}
            {inv.status !== "draft" && (
              <InvoiceDeliveryActions
                kind="rental"
                invoiceId={inv.id}
                invoiceNumber={inv.invoice_number}
                variant="full"
                className="mr-1"
              />
            )}
            {editable && (
              <Button variant="outline" onClick={onSave} disabled={!dirty || saving} data-testid="invoice-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                Save draft
              </Button>
            )}
            {editable && inv.status === "draft" && (
              <Button onClick={onIssue} disabled={saving || items.length === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="invoice-issue-btn">
                <Send className="h-4 w-4 mr-1.5" /> Issue to customer
              </Button>
            )}
            {editable && inv.status !== "draft" && inv.status !== "settled" && (
              <Button onClick={onSettle} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="invoice-settle-btn">
                Mark settled
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border shadow-sm">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div className="text-sm font-semibold text-neutral-900">Line items</div>
            {editable && (
              <Button variant="ghost" size="sm" onClick={onAddLine} className="text-indigo-600 hover:text-indigo-900" data-testid="invoice-add-line-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Add line
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-3 w-32">Type</th>
                  <th className="text-left px-4 py-3">Description</th>
                  <th className="text-right px-4 py-3 w-20">Qty</th>
                  <th className="text-right px-4 py-3 w-28">Unit (€)</th>
                  <th className="text-right px-4 py-3 w-24">VAT %</th>
                  <th className="text-right px-4 py-3 w-28">Line total</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500">No line items yet.</td></tr>
                )}
                {items.map((li, idx) => {
                  const lineNet = Number(li.qty || 0) * Number(li.unit_price || 0);
                  const lineVat = lineNet * (Number(li.vat_rate || 0) / 100);
                  return (
                    <tr key={li.id || idx} className="border-b border-border last:border-0" data-testid={`invoice-line-${idx}`}>
                      <td className="px-4 py-2.5">
                        {editable ? (
                          <Select value={li.kind} onValueChange={(v) => onLineChange(idx, "kind", v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`invoice-line-kind-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {LINE_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (<span className="text-xs text-neutral-600">{LINE_KINDS.find((k) => k.value === li.kind)?.label || li.kind}</span>)}
                      </td>
                      <td className="px-4 py-2.5">
                        {editable ? (
                          <Input
                            value={li.label || ""}
                            onChange={(e) => onLineChange(idx, "label", e.target.value)}
                            className="h-8 text-sm"
                            placeholder="Description"
                            data-testid={`invoice-line-label-${idx}`}
                          />
                        ) : (<span className="text-sm text-neutral-800">{li.label}</span>)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {editable ? (
                          <Input
                            type="number" step="0.5" min="0"
                            value={li.qty}
                            onChange={(e) => onLineChange(idx, "qty", e.target.value)}
                            className="h-8 text-sm text-right"
                            data-testid={`invoice-line-qty-${idx}`}
                          />
                        ) : (<span className="text-sm">{li.qty}</span>)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {editable ? (
                          <Input
                            type="number" step="0.01"
                            value={li.unit_price}
                            onChange={(e) => onLineChange(idx, "unit_price", e.target.value)}
                            className="h-8 text-sm text-right"
                            data-testid={`invoice-line-unit-${idx}`}
                          />
                        ) : (<Money amount={li.unit_price} />)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {editable ? (
                          <Input
                            type="number" step="1" min="0" max="100"
                            value={li.vat_rate}
                            onChange={(e) => onLineChange(idx, "vat_rate", e.target.value)}
                            className="h-8 text-sm text-right"
                            data-testid={`invoice-line-vat-${idx}`}
                          />
                        ) : (<span className="text-sm">{li.vat_rate}%</span>)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-sm">
                        <Money amount={lineNet + lineVat} />
                      </td>
                      <td className="px-2 py-2.5">
                        {editable && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:bg-rose-50" onClick={() => onRemoveLine(idx)} data-testid={`invoice-line-delete-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-neutral-50/60 border-t border-border">
                <tr className="text-sm">
                  <td colSpan={5} className="px-4 py-2.5 text-right text-neutral-600">Subtotal (net)</td>
                  <td className="px-4 py-2.5 text-right font-medium" data-testid="invoice-totals-subtotal"><Money amount={totals.subtotal_net} /></td>
                  <td></td>
                </tr>
                <tr className="text-sm">
                  <td colSpan={5} className="px-4 py-2.5 text-right text-neutral-600">VAT</td>
                  <td className="px-4 py-2.5 text-right font-medium" data-testid="invoice-totals-vat"><Money amount={totals.vat_total} /></td>
                  <td></td>
                </tr>
                <tr className="text-base">
                  <td colSpan={5} className="px-4 py-3 text-right font-semibold text-neutral-900">Total</td>
                  <td className="px-4 py-3 text-right font-display text-lg font-semibold" data-testid="invoice-totals-total"><Money amount={totals.total_gross} /></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <div>
            <Label htmlFor="invoice-vat-id">VAT / Tax ID</Label>
            <Input
              id="invoice-vat-id"
              value={vatId}
              onChange={(e) => { setVatId(e.target.value); setDirty(true); }}
              disabled={!editable}
              placeholder="e.g. RO12345678"
              className="mt-1 rounded-xl"
              data-testid="invoice-vat-id"
            />
          </div>
          <div>
            <Label htmlFor="invoice-customer-note">Note to customer</Label>
            <Textarea
              id="invoice-customer-note"
              value={customerNote}
              onChange={(e) => { setCustomerNote(e.target.value); setDirty(true); }}
              disabled={!editable}
              placeholder="Optional · printed on the customer's invoice copy"
              className="mt-1 rounded-xl"
              rows={3}
              data-testid="invoice-customer-note"
            />
          </div>
        </div>

        <div className="mt-6 text-xs text-neutral-500 flex items-center gap-1.5">
          <History className="h-3.5 w-3.5" />
          Edits create immutable audit revisions · this invoice locks 30 days after the rental end date.
        </div>
      </main>
      <Footer />
    </div>
  );
}
